import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";

import React, { useCallback, useMemo, useState } from "react";
import { updateMultipleRequest } from "../../services/inventoryService";
import { updateTransferPicked } from "../../services/transferService";

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
  invoice: any;
  products: any[]; // fetchProductList result
  setProducts: React.Dispatch<React.SetStateAction<any[]>>;
  selectedProducts: any[];
  setSelectedProducts: React.Dispatch<React.SetStateAction<any[]>>;
  expandedCategories: string | null;
  setExpandedCategories: React.Dispatch<React.SetStateAction<string | null>>;
  disabledQty: boolean;
}

export const COLORS = {
  bg: "#F5F7FA",
  surface: "#FFFFFF",
  border: "#E5E7EB",
  text: "#111827",
  textSoft: "#6B7280",
  primary: "#1D4ED8",
  primaryLight: "#EFF6FF",
  danger: "#DC2626",
  success: "#059669",
};

const EditTransferModal = ({
  open,
  onClose,
  onSaved,
  invoice,
  products,
  setProducts,
  selectedProducts,
  setSelectedProducts,
  expandedCategories,
  setExpandedCategories,
  disabledQty = false,
}: Props) => {
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [productPanelOpen, setProductPanelOpen] = useState(true);
  const isMobile = useMediaQuery("(max-width:600px)");
  const [addingProduct, setAddingProduct] = useState(false);
  const [deletingProductId, setDeletingProductId] = useState<string | null>(
    null,
  );

  const totals = useMemo(() => {
    const activeProducts = selectedProducts.filter(
      (item) => item._ui_action !== "DELETED",
    );

    return {
      totalItems: activeProducts.length,
      totalQty: activeProducts.reduce(
        (a, b) => a + Number(b.qty_packed || 0),
        0,
      ),
      pickedItem: activeProducts.filter((p) => p.picked).length,
    };
  }, [selectedProducts]);

  const toggleCategory = useCallback(
    (category: string) => {
      setExpandedCategories(category === expandedCategories ? null : category);
    },
    [expandedCategories],
  );

  const addSelectedProduct = async (value: any) => {
    if (!value || addingProduct) return;

    const product = value.value;

    const exists = selectedProducts.some(
      (p: any) =>
        p.product_id === product.product_id && p._ui_action !== "DELETED",
    );

    if (exists) {
      alert("Product already added.");
      return;
    }

    const deletedItem = selectedProducts.find(
      (p: any) =>
        p.product_id === product.product_id && p._ui_action === "DELETED",
    );

    /*
     * If this product was only marked as deleted in the UI
     * but the delete has NOT been saved yet, it already exists
     * in the database. Just restore it locally.
     */
    if (deletedItem?.transfer_id) {
      setSelectedProducts((prev: any[]) =>
        prev.map((item: any) =>
          item.product_id === product.product_id
            ? {
                ...item,
                qty_packed: item.qty_packed || 1,
                qty_unpacked: item.qty_unpacked || 0,
                notes: invoice.notes,
                _ui_action: "UPDATED",
              }
            : item,
        ),
      );

      setSelectedItem(null);
      return;
    }

    const formattedProduct = {
      transfer_id: null,
      invoice_no: invoice.invoice_no,

      product_id: product.product_id,
      product: product.name,

      category: product.category,
      sub_category: product.sub_category,

      unit: product.unit,
      packed: product.packed,

      sending_store: invoice.sending_store,
      receiving_store: invoice.receiving_store,

      qty_packed: 1,
      qty_unpacked: 0,

      status: "PENDING",
      notes: invoice.notes,

      created_at: null,
      updated_at: null,

      requested_by: invoice.requested_by,

      email: null,
      first_name: null,
      last_name: null,

      store: invoice.store,

      _ui_action: "NEW",
    };

    const payload = {
      create: [formattedProduct],
      update: [],
      del: [],
    };

    try {
      setAddingProduct(true);

      const result = await updateMultipleRequest(
        payload,
        invoice?.invoice_no,
        invoice?.sending_store,
        invoice?.requested_by,
      );

      const createdTransfer = result?.[0];

      if (!createdTransfer?.transfer_id) {
        throw new Error("Created transfer did not return a transfer_id.");
      }

      const savedProduct = {
        ...formattedProduct,
        ...createdTransfer,

        // Keep product display information
        product: product.name,
        category: product.category,
        sub_category: product.sub_category,
        unit: product.unit,
        packed: product.packed,
        store: invoice.store,

        _ui_action: undefined,
      };

      setSelectedProducts((prev: any[]) => {
        const withoutExisting = prev.filter(
          (item: any) => item.product_id !== product.product_id,
        );

        return [savedProduct, ...withoutExisting];
      });

      alert("Updated successfully.");
      setSelectedItem(null);

      await onSaved();
    } catch (error) {
      console.error("Failed to add product", error);
      alert("Failed to add product.");
    } finally {
      setAddingProduct(false);
    }
  };

  const handleQtyChange = (productId: string, qty: number) => {
    setSelectedProducts((prev: any) =>
      prev.map((item: any) =>
        item.product_id === productId
          ? {
              ...item,
              qty_packed: qty, // allow 0, but don't delete yet
              _ui_action: item._ui_action === "NEW" ? "NEW" : "UPDATED",
            }
          : item,
      ),
    );
  };

  // Unpacked qty
  const handleUnpackedQtyChange = (productId: string, qty: number) => {
    setSelectedProducts((prev: any) =>
      prev.map((item: any) =>
        item.product_id === productId
          ? {
              ...item,
              qty_unpacked: qty, // allow 0, but don't delete yet
              _ui_action: item._ui_action === "NEW" ? "NEW" : "UPDATED",
            }
          : item,
      ),
    );
  };

  const handleRemove = async (productId: string) => {
    const item = selectedProducts.find(
      (product: any) => product.product_id === productId,
    );

    if (!item) return;

    const confirm = window.confirm(
      `Are you sure you want to delete ${item.product}?`,
    );

    if (!confirm) return;

    /*
     * If for some reason the product has not been saved yet,
     * just remove it locally.
     */
    if (!item.transfer_id) {
      setSelectedProducts((prev: any[]) =>
        prev.filter((product: any) => product.product_id !== productId),
      );
      return;
    }

    const payload = {
      create: [],
      update: [],
      del: [item],
    };

    try {
      setDeletingProductId(productId);

      await updateMultipleRequest(
        payload,
        invoice?.invoice_no,
        invoice?.sending_store,
        invoice?.requested_by,
      );

      // Only remove from visible UI after DB delete succeeded
      setSelectedProducts((prev: any[]) =>
        prev.filter((product: any) => product.product_id !== productId),
      );
      /*
       * Refresh from DB after deletion.
       */
      await onSaved();
    } catch (error) {
      console.error("Failed to delete product", error);
      alert("Failed to delete product.");
    } finally {
      setDeletingProductId(null);
    }
  };

  // const handleSave = async () => {
  //   const activeProducts = selectedProducts.filter(
  //     (item) => item._ui_action !== "DELETED" && Number(item.qty_packed) > 0,
  //   );

  //   if (activeProducts.length === 0) {
  //     alert("At least one product must remain in the transfer request.");
  //     return;
  //   }

  //   const payload = {
  //     create: selectedProducts.filter(
  //       (item) => item._ui_action === "NEW" && Number(item.qty_packed) > 0,
  //     ),

  //     update: selectedProducts.filter(
  //       (item) => item._ui_action === "UPDATED" && Number(item.qty_packed) > 0,
  //     ),

  //     del: selectedProducts.filter(
  //       (item) => item._ui_action === "DELETED" || Number(item.qty_packed) <= 0,
  //     ),
  //   };

  //   if (
  //     payload.create.length === 0 &&
  //     payload.update.length === 0 &&
  //     payload.del.length === 0
  //   ) {
  //     alert("No changes to save.");
  //     return;
  //   }

  //   try {
  //     setSaving(true);

  //     await updateMultipleRequest(
  //       payload,
  //       invoice?.invoice_no,
  //       invoice?.sending_store,
  //       invoice?.requested_by,
  //     );

  //     await onSaved();

  //     alert("Transfer request updated successfully.");

  //     onClose();
  //     // empty the expanded categories state or turn all of them to false
  //     setExpandedCategories(null);
  //   } catch (error) {
  //     console.error(error);
  //     alert("Failed to update transfer request.");
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  const visibleProducts = useMemo(
    () =>
      // selectedProducts
      //   .filter((item) => item._ui_action !== "DELETED")
      //   .sort((a, b) => {
      //     // based on category, then sub_category, then product name
      //     if (a.category < b.category) return -1;
      //     if (a.category > b.category) return 1;
      //     return 0;
      //   }),
      selectedProducts
        .filter((item) => item._ui_action !== "DELETED")
        .sort((a, b) => {
          const categoryCompare = (a.category || "").localeCompare(
            b.category || "",
          );

          if (categoryCompare !== 0) {
            return categoryCompare;
          }

          const subCategoryCompare = (a.sub_category || "").localeCompare(
            b.sub_category || "",
          );

          if (subCategoryCompare !== 0) {
            return subCategoryCompare;
          }

          return (a.product || "").localeCompare(b.product || "");
        }),
    [selectedProducts],
  );

  // Group the categories
  const groupedProducts = useMemo(
    () =>
      visibleProducts.reduce<Record<string, typeof products>>(
        (acc, product) => {
          (acc[product.category] ??= []).push(product);
          return acc;
        },
        {},
      ),
    [visibleProducts],
  );

  const categoryStats = useMemo(() => {
    const stats: Record<
      string,
      {
        totalProducts: number;
        pickedProducts: number;
        pickedQty: number;
      }
    > = {};

    Object.entries(groupedProducts).forEach(([category, categoryProducts]) => {
      const pickedProducts = categoryProducts.filter((item) =>
        Boolean(item.picked),
      );

      stats[category] = {
        totalProducts: categoryProducts.length,
        pickedProducts: pickedProducts.length,
        pickedQty: pickedProducts.reduce(
          (total, item) => total + Number(item.qty_packed ?? 0),
          0,
        ),
      };
    });

    return stats;
  }, [groupedProducts]);

  const saveButtonDisabled = useMemo(() => {
    const hasChanges = selectedProducts.some(
      (item) =>
        item._ui_action === "NEW" ||
        item._ui_action === "UPDATED" ||
        item._ui_action === "DELETED",
    );
    return !hasChanges;
  }, [selectedProducts]);

  /**
   * Persist the picked state together with the quantities currently entered in
   * the UI. The API response is treated as the source of truth so the drawer
   * always reflects the values that were actually saved by the backend.
   */
  const handlePickedChange = async (item: any, picked: boolean) => {
    if (!item?.transfer_id) return;

    try {
      const result = await updateTransferPicked(
        item.transfer_id,
        picked,
        Number(item.qty_packed ?? 0),
        Number(item.qty_unpacked ?? 0),
      );

      if (
        !result ||
        !("picked" in result) ||
        !("qty_packed" in result) ||
        !("qty_unpacked" in result)
      ) {
        throw new Error(
          "Picked update response is missing picked or quantity values.",
        );
      }

      const updatedValues = {
        picked: Boolean(result.picked),
        qty_packed: Number(result.qty_packed ?? 0),
        qty_unpacked: Number(result.qty_unpacked ?? 0),
      };

      setSelectedProducts((prev: any) =>
        prev.map((product: any) =>
          product.transfer_id === item.transfer_id
            ? { ...product, ...updatedValues }
            : product,
        ),
      );

      setProducts((prev: any[]) =>
        prev.map((product) =>
          product.transfer_id === item.transfer_id
            ? { ...product, ...updatedValues }
            : product,
        ),
      );

      if (updatedValues.picked) {
        alert(` ${item.product} picked.`);
      } else {
        alert(` ${item.product} marked as unpicked.`);
      }
    } catch (error) {
      console.error("Failed to update picked status and quantities", error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          background: COLORS.bg,
        }}
      >
        <Box
          sx={{
            minHeight: 72,
            px: {
              xs: 1.5,
              md: 3,
            },
            py: 1,
            borderBottom: `1px solid ${COLORS.border}`,
            background: COLORS.surface,
            display: "flex",
            position: "sticky",
            top: 0,
            zIndex: 10,
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 22,
                fontWeight: 700,
                color: COLORS.text,
              }}
            >
              Edit Transfer Request
            </Typography>

            <Typography
              sx={{
                fontSize: 13,
                color: COLORS.textSoft,
                mt: 0.3,
              }}
            >
              Invoice #{invoice?.invoice_no}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            {totals.pickedItem === totals.totalItems &&
            totals.totalItems > 0 ? (
              <Chip
                label="All products picked"
                sx={{
                  background: COLORS.primaryLight,
                  color: COLORS.success,
                }}
              />
            ) : (
              <Chip
                label={`${totals.pickedItem} / ${totals.totalItems} Picked products`}
                sx={{
                  background: COLORS.primaryLight,
                  color: COLORS.primary,
                  fontWeight: 600,
                }}
              />
            )}

            <Chip
              label={`${totals.totalQty} Qty`}
              sx={{
                background: "#ECFDF5",
                color: COLORS.success,
                fontWeight: 600,
              }}
            />

            {/* <Button
              variant="contained"
              startIcon={<SaveOutlinedIcon />}
              onClick={handleSave}
              disabled={saving || saveButtonDisabled}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                px: 2.5,
                fontWeight: 600,
                boxShadow: "none",
              }}
            >
              {saving ? "Saving..." : "Save"}
            </Button> */}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            overflow: "hidden",
            flexDirection: {
              xs: "column",
              md: "row",
            },
          }}
        >
          <Box
            sx={{
              width: {
                // xs: productPanelOpen ? "100%" : 60,
                md: 350,
              },
              borderRight: {
                xs: "none",
                md: `1px solid ${COLORS.border}`,
              },
              borderBottom: {
                xs: `1px solid ${COLORS.border}`,
                md: "none",
              },
              background: COLORS.surface,
              p: {
                xs: 1.5,
                md: 2,
              },
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              Add Products
              <IconButton
                onClick={() => setProductPanelOpen(!productPanelOpen)}
                size="small"
              >
                {productPanelOpen ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>
            </Typography>
            <Collapse
              in={productPanelOpen}
              unmountOnExit
              orientation="vertical"
            >
              <Autocomplete
                sx={{ width: "100%", flexGrow: 1 }}
                options={products.map((p) => ({
                  label: p.name,
                  value: p,
                }))}
                value={selectedItem}
                onChange={(_, value) => {
                  setSelectedItem(value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search products..."
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Button
                fullWidth
                variant="contained"
                disabled={!selectedItem || addingProduct}
                onClick={() => addSelectedProduct(selectedItem)}
                sx={{
                  height: 46,
                  textTransform: "none",
                  borderRadius: "10px",
                  fontWeight: 600,
                }}
              >
                {addingProduct ? "Adding..." : "Add Product"}
              </Button>

              <Divider />

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: "14px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    color: COLORS.textSoft,
                  }}
                >
                  Store
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 600,
                    // mb: 2,
                  }}
                >
                  {invoice?.store}
                </Typography>

                {/* <Typography
                sx={{
                  fontSize: 13,
                  color: COLORS.textSoft,
                }}
              >
                Receiving Store
              </Typography>

              <Typography
                sx={{
                  fontWeight: 600,
                }}
              >
                {invoice?.receiving_store}
              </Typography> */}
              </Paper>
            </Collapse>
          </Box>

          <Box
            sx={{
              flex: 1,
              p: 3,
              overflow: "auto",
            }}
          >
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: "16px",
                overflowX: "auto",
                border: `1px solid ${COLORS.border}`,
              }}
            >
              {isMobile ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {Object.entries(groupedProducts).map(
                    ([category, categoryProducts]) => (
                      <Box key={category}>
                        {/* Category header */}
                        <Box
                          onClick={() => toggleCategory(category)}
                          sx={{
                            bgcolor:
                              expandedCategories === category
                                ? "#49a379"
                                : "#ffb485",
                            cursor: "pointer",
                            px: 2,
                            py: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderRadius: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {expandedCategories === category ? (
                              <KeyboardArrowDownIcon />
                            ) : (
                              <KeyboardArrowRightIcon />
                            )}

                            <Typography sx={{ fontWeight: 700 }}>
                              {category}
                            </Typography>
                          </Box>

                          {/* <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Chip
                              size="small"
                              label={`${categoryProducts.length} Products`}
                            />

                            <Chip
                              size="small"
                              label={`${categoryStats[category]?.pickedProducts ?? 0} / ${
                                categoryStats[category]?.totalProducts ?? 0
                              } Picked`}
                            />

                            <Chip
                              size="small"
                              label={`${categoryStats[category]?.pickedQty ?? 0} Packs Picked`}
                            />
                          </Box> */}
                        </Box>

                        {/* Product cards */}
                        <Collapse
                          in={expandedCategories === category}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.5,
                              pt: 1.5,
                            }}
                          >
                            {categoryProducts.map((item) => (
                              <Paper
                                key={
                                  item.transfer_id ?? `new-${item.product_id}`
                                }
                                elevation={0}
                                sx={{
                                  p: 2,
                                  borderRadius: 3,
                                  border: `1px solid ${COLORS.border}`,
                                  bgcolor: item._ui_action ? "#E8F5E9" : "#fff",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 1.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                      flex: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 2,
                                        bgcolor: COLORS.primaryLight,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                      }}
                                    >
                                      <Inventory2OutlinedIcon
                                        sx={{ color: COLORS.primary }}
                                      />
                                    </Box>

                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        sx={{
                                          fontWeight: 700,
                                          fontSize: 15,
                                          overflowWrap: "anywhere",
                                        }}
                                      >
                                        {item.product}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.25 }}
                                      >
                                        {item.sub_category || "No sub-category"}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  <IconButton
                                    onClick={() =>
                                      handleRemove(item.product_id)
                                    }
                                    disabled={
                                      Boolean(item.picked) ||
                                      deletingProductId === item.product_id
                                    }
                                    size="small"
                                    sx={{ color: COLORS.danger, flexShrink: 0 }}
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </Box>

                                <Divider sx={{ my: 1.5 }} />

                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 1.5,
                                    mb: 1.5,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block", mb: 0.5 }}
                                    >
                                      Requested
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={`${item.requested_qty_packed ?? item.qty_packed ?? 0} packs`}
                                      sx={{ fontWeight: 600 }}
                                    />
                                  </Box>

                                  <Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block", mb: 0.5 }}
                                    >
                                      Pack size
                                    </Typography>
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={`${item.packed ?? 0} ${item.unit ?? ""}`.trim()}
                                      sx={{ fontWeight: 600 }}
                                    />
                                  </Box>
                                </Box>

                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns:
                                      "minmax(0, 1fr) minmax(0, 1fr) auto",
                                    gap: 1,
                                    alignItems: "end",
                                  }}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block", mb: 0.5 }}
                                    >
                                      Given Qty
                                    </Typography>
                                    <TextField
                                      fullWidth
                                      type="number"
                                      size="small"
                                      disabled={
                                        Boolean(item.picked) || disabledQty
                                      }
                                      value={item.qty_packed ?? 0}
                                      onChange={(e) =>
                                        handleQtyChange(
                                          item.product_id,
                                          Number(e.target.value),
                                        )
                                      }
                                      slotProps={{
                                        htmlInput: { min: 0 },
                                      }}
                                    />
                                  </Box>

                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block", mb: 0.5 }}
                                    >
                                      Unpacked Qty
                                    </Typography>
                                    <TextField
                                      fullWidth
                                      type="number"
                                      size="small"
                                      disabled={
                                        Boolean(item.picked) || disabledQty
                                      }
                                      value={item.qty_unpacked ?? 0}
                                      onChange={(e) =>
                                        handleUnpackedQtyChange(
                                          item.product_id,
                                          Number(e.target.value),
                                        )
                                      }
                                      slotProps={{
                                        htmlInput: { min: 0 },
                                      }}
                                    />
                                  </Box>

                                  <Box
                                    sx={{ textAlign: "center", minWidth: 52 }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block", mb: 0.25 }}
                                    >
                                      Picked
                                    </Typography>
                                    <Checkbox
                                      checked={Boolean(item.picked)}
                                      disabled={!item.transfer_id}
                                      onChange={(e) =>
                                        handlePickedChange(
                                          item,
                                          e.target.checked,
                                        )
                                      }
                                    />
                                  </Box>
                                </Box>
                              </Paper>
                            ))}
                          </Box>
                        </Collapse>
                      </Box>
                    ),
                  )}
                </Box>
              ) : (
                <Table
                  stickyHeader
                  sx={{
                    minWidth: {
                      xs: 900,
                      md: "auto",
                    },
                  }}
                >
                  <TableBody>
                    {Object.entries(groupedProducts).map(
                      ([category, products]) => (
                        <React.Fragment key={category}>
                          {/* Category Row */}
                          <TableRow
                            sx={{
                              bgcolor:
                                (categoryStats[category]?.pickedProducts ??
                                  0) ===
                                (categoryStats[category]?.totalProducts ?? 0)
                                  ? "#49a379"
                                  : expandedCategories === category
                                    ? "#ffb485"
                                    : "#f0f0f0",
                              // bgcolor:
                              //   expandedCategories === category
                              //     ? "#49a379"
                              //     : "#ffb485",
                              cursor: "pointer",
                            }}
                            onClick={() => toggleCategory(category)}
                          >
                            <TableCell colSpan={6}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {expandedCategories === category ? (
                                  <KeyboardArrowDownIcon />
                                ) : (
                                  <KeyboardArrowRightIcon />
                                )}

                                <Typography fontWeight={700}>
                                  {category}
                                </Typography>

                                <Chip
                                  size="small"
                                  label={`${categoryStats[category]?.pickedProducts ?? 0} / ${
                                    categoryStats[category]?.totalProducts ?? 0
                                  } Picked`}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* Products */}
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              sx={{
                                p: 0,
                                border: 0,
                              }}
                            >
                              <Collapse
                                in={expandedCategories === category}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Table size="small">
                                  <TableBody>
                                    <TableRow>
                                      <TableCell>Product</TableCell>
                                      <TableCell>Sub Category</TableCell>
                                      <TableCell align="center">
                                        Pack size
                                      </TableCell>
                                      <TableCell align="center">
                                        Requested Qty
                                      </TableCell>
                                      <TableCell align="center">
                                        Given Qty
                                      </TableCell>
                                      <TableCell align="center">
                                        Unpacked Qty
                                      </TableCell>
                                      <TableCell align="center">
                                        Picked
                                      </TableCell>
                                      <TableCell align="center">
                                        Remove
                                      </TableCell>
                                    </TableRow>
                                    {visibleProducts
                                      .filter(
                                        (item) => item.category === category,
                                      )
                                      .map((item) => (
                                        <>
                                          <TableRow
                                            key={
                                              item.transfer_id ??
                                              `new-${item.product_id}`
                                            }
                                            sx={{
                                              backgroundColor: item._ui_action
                                                ? "#E8F5E9"
                                                : undefined,
                                            }}
                                          >
                                            <TableCell>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1.5,
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    width: 42,
                                                    height: 42,
                                                    borderRadius: "12px",
                                                    background:
                                                      COLORS.primaryLight,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                  }}
                                                >
                                                  <Inventory2OutlinedIcon
                                                    sx={{
                                                      color: COLORS.primary,
                                                    }}
                                                  />
                                                </Box>

                                                <Box>
                                                  <Typography
                                                    sx={{
                                                      fontWeight: 600,
                                                      fontSize: 14,
                                                    }}
                                                  >
                                                    {item.product}
                                                  </Typography>

                                                  {/* <Typography
                                                    sx={{
                                                      fontSize: 12,
                                                      color: COLORS.textSoft,
                                                    }}
                                                  >
                                                    SKU: {item.product_id}
                                                  </Typography> */}
                                                </Box>
                                              </Box>
                                            </TableCell>

                                            <TableCell>
                                              {item.sub_category}
                                            </TableCell>

                                            <TableCell align="center">
                                              <Typography
                                                variant="body2"
                                                color="textDisabled"
                                              >
                                                {item.packed} {item.unit}
                                              </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                              <Typography
                                                variant="body2"
                                                color="textDisabled"
                                              >
                                                {item.requested_qty_packed}{" "}
                                                Packs
                                              </Typography>
                                            </TableCell>

                                            <TableCell align="center">
                                              <TextField
                                                type="number"
                                                size="small"
                                                disabled={
                                                  Boolean(item.picked) ||
                                                  disabledQty
                                                }
                                                value={item.qty_packed ?? 0}
                                                onChange={(e) =>
                                                  handleQtyChange(
                                                    item.product_id,
                                                    Number(e.target.value),
                                                  )
                                                }
                                                sx={{
                                                  width: {
                                                    xs: 80,
                                                    md: 120,
                                                  },
                                                }}
                                              />
                                            </TableCell>
                                            <TableCell align="center">
                                              <TextField
                                                type="number"
                                                size="small"
                                                disabled={
                                                  Boolean(item.picked) ||
                                                  disabledQty
                                                }
                                                value={item.qty_unpacked ?? 0}
                                                onChange={(e) =>
                                                  handleUnpackedQtyChange(
                                                    item.product_id,
                                                    Number(e.target.value),
                                                  )
                                                }
                                                sx={{
                                                  maxWidth: {
                                                    xs: 80,
                                                    md: 120,
                                                  },
                                                }}
                                              />
                                            </TableCell>

                                            <TableCell align="center">
                                              <Checkbox
                                                checked={Boolean(item.picked)}
                                                onChange={(e) =>
                                                  handlePickedChange(
                                                    item,
                                                    e.target.checked,
                                                  )
                                                }
                                              />
                                            </TableCell>
                                            <TableCell align="center">
                                              <IconButton
                                                onClick={() =>
                                                  handleRemove(item.product_id)
                                                }
                                                disabled={
                                                  Boolean(item.picked) ||
                                                  deletingProductId ===
                                                    item.product_id
                                                }
                                                sx={{
                                                  color: COLORS.danger,
                                                }}
                                              >
                                                <DeleteOutlineIcon />
                                              </IconButton>
                                            </TableCell>
                                          </TableRow>
                                        </>
                                      ))}
                                  </TableBody>
                                </Table>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ),
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default EditTransferModal;
